import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useStoreManagementStore } from "@/store/storeManagementStore";
import { useOrderStore } from "@/store/orderStore";
import { OrdersList } from "@/components/orders/OrdersList";
import { ActivityLogTable } from "@/components/activity/ActivityLogTable";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { useIsMobile } from "@/hooks/use-mobile";

const OrdersManagement = () => {
  const { stores } = useStoreManagementStore();
  const {
    orders,
    fetchOrders,
    isLoading,
    setSelectedStoreId,
    subscribeToOrdersRealtime,
    unsubscribeFromOrdersRealtime,
    activityCount,
    subscribeToActivitiesRealtime,
    unsubscribeFromActivitiesRealtime,
    addOrder,
  } = useOrderStore();
  const { selectedStoreId } = useStoreManagementStore();
  const [activeTab, setActiveTab] = useState("pending");
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  useEffect(() => {
    // Set default store if only one exists
    if (stores.length === 1 && !selectedStoreId) {
      setSelectedStoreId(stores[0].id);
    }
  }, [stores, selectedStoreId, setSelectedStoreId]);

  useEffect(() => {
    if (selectedStoreId) {
      // Debug alert - removed in production but useful for debugging
      // alert('Store ID changed: ' + selectedStoreId);
      // Fetch orders whenever selectedStoreId changes
      fetchOrders(selectedStoreId);

      subscribeToOrdersRealtime(selectedStoreId);
      subscribeToActivitiesRealtime(selectedStoreId);

      // Cleanup subscription when component unmounts or store changes
      return () => {
        console.log("Cleaning up real-time subscription");
        unsubscribeFromOrdersRealtime();
        unsubscribeFromActivitiesRealtime();
      };
    }
  }, [
    selectedStoreId,
    fetchOrders,
    subscribeToOrdersRealtime,
    unsubscribeFromOrdersRealtime,
    subscribeToActivitiesRealtime,
    unsubscribeFromActivitiesRealtime,
  ]);

  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
  };

  const handleOrderClick = (orderId: string) => {
    // setSelectedOrderId(orderId);
  };

  // Filter orders based on active tab
  const filteredOrders = orders.filter((order) => {
    if (activeTab === "pending")
      return order.status === "pending" || order.status === "preparing";
    if (activeTab === "completed") return order.status === "ready";
    if (activeTab === "delivered")
      return order.status === "delivered" || order.status === "completed";
    if (activeTab === "canceled") return order.status === "cancelled";
    return true;
  });

  // Show a toast notification when a new order comes in
  useEffect(() => {
    const pendingOrdersCount = orders.filter(
      (order) => order.status === "pending" || order.status === "preparing"
    ).length;

    if (pendingOrdersCount > 0 && activeTab === "pending") {
      // This will run on initial render too, so we need to be careful
      // Only showing toast after the component has mounted and real-time updates kick in
      const timeoutId = setTimeout(() => {
        // Do nothing - this is just to ensure we don't show a toast immediately on page load
      }, 2000);

      return () => clearTimeout(timeoutId);
    }
  }, [orders, activeTab]);

  return (
    <div className="space-y-4 px-2 sm:px-0">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight">
            {t("ordersManagement.title")}
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            {t("manageYourStoreTablesAndQrCodes")}
          </p>
        </div>
      </div>

      {selectedStoreId ? (
        <div className="space-y-4">
          <div className="flex overflow-auto pb-1 -mx-2 px-2">
            <div className="flex border-b border-muted w-full min-w-max">
              <button
                className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                  activeTab === "pending"
                    ? "border-b-2 border-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleTabChange("pending")}
              >
                {t("ordersManagement.pendingOrders")}
              </button>
              <button
                className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                  activeTab === "completed"
                    ? "border-b-2 border-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleTabChange("completed")}
              >
                {t("ordersManagement.completedOrders")}
              </button>
              <button
                className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                  activeTab === "delivered"
                    ? "border-b-2 border-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleTabChange("delivered")}
              >
                {t("ordersManagement.deliveredOrders")}
              </button>
              <button
                className={`px-3 py-2 text-sm transition-colors whitespace-nowrap ${
                  activeTab === "canceled"
                    ? "border-b-2 border-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleTabChange("canceled")}
              >
                {t("ordersManagement.canceledOrders")}
              </button>
              <button
                className={`px-3 py-2 text-sm transition-colors relative whitespace-nowrap ${
                  activeTab === "activity"
                    ? "border-b-2 border-primary font-medium"
                    : "text-muted-foreground hover:text-foreground"
                }`}
                onClick={() => handleTabChange("activity")}
              >
                {t("ordersManagement.orderActivity")}
                {activityCount > 0 && activeTab !== "activity" && (
                  <Badge className="absolute -top-2 -right-1 h-5 min-w-[20px] bg-red-500">
                    {activityCount}
                  </Badge>
                )}
              </button>
            </div>
          </div>

          {activeTab === "activity" ? (
            <Card>
              <CardHeader className="pb-3 pt-4 px-4 sm:px-6 sm:pt-6">
                <CardTitle className="text-xl flex items-center gap-2">
                  {t("ordersManagement.recentOrderActivity")}
                  {activityCount > 0 && (
                    <Badge className="bg-red-500">
                      {activityCount} {t("new")}
                    </Badge>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-2 sm:px-6">
                <ActivityLogTable
                  storeId={selectedStoreId}
                  entityType="order"
                />
              </CardContent>
            </Card>
          ) : (
            <OrdersList
              orders={filteredOrders}
              isLoading={isLoading}
              storeId={selectedStoreId}
              onOrderClick={handleOrderClick}
            />
          )}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardHeader>
            <CardTitle>{t("selectStore")}</CardTitle>
          </CardHeader>
        </Card>
      )}
    </div>
  );
};

export default OrdersManagement;
